import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { addBusiness } from '../store/slices/businessSlice'
import { onCompanyDetails, onClaimCompany } from '../lib/api'
import { Company } from '../lib/db/db.types'
import { BusinessDetails } from '../components/business/BusinessDetails'
import { transformBusiness } from '../lib/db/db.transforms'
import { SceneContainer } from '../components/scene/SceneContainer'
import { setCurrentWorkspace } from '../store/slices/workspaceSlice'

const RegisterBusinessScreen = () => {
  const { companyNumber } = useParams()

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [business, setBusiness] = useState<Company | null>(null)
  const [selectedOfficerId, setSelectedOfficerId] = useState<string>('')

  const getBusinessDetails = useCallback(
    async ({ companyNumber }: { companyNumber: string }) => {
      if (!companyNumber) return
      try {
        const details = await onCompanyDetails({
          companyNumber,
        })
        setBusiness(details.data)
      } catch (error) {
        console.error('Error fetching officers:', error)
      }
    },
    [],
  )

  const handleBack = () => {
    navigate('/new-business')
  }

  const handleClaimBusiness = async () => {
    setError(null)

    if (!business || !selectedOfficerId) return

    setIsLoading(true)

    const result = await onClaimCompany({
      companyNumber: business.companyNumber,
      personNumber: selectedOfficerId,
    })

    if (result.data.status === 'OK') {
      dispatch(addBusiness(transformBusiness(result.data.data)))
      navigate(`/setup-business/${result.data.data.id}`)
    } else {
      setError(result.data.error)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (companyNumber) {
      getBusinessDetails({ companyNumber })
    }
  }, [companyNumber, getBusinessDetails])

  return (
    <SceneContainer>
      <div className="flex flex-col mb-4 gap-2">
        <h1 className="text-2xl font-bold">Register business</h1>
        {!business && (
          <Link
            to="/new-business"
            className="text-blue-500 hover:text-blue-600"
          >
            ← Back to search
          </Link>
        )}
      </div>

      {business ? (
        <div className="bg-gray-50 p-6 rounded-lg border">
          <h2 className="text-xl font-semibold mb-4">
            Selected Business Details
          </h2>

          <BusinessDetails
            name={business.title}
            companyNumber={business.companyNumber}
            companyStatus={business.companyStatus}
            dateOfCreation={business.dateOfCreation}
            address={business.address}
          />

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Select Your Position *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {business.officers
                .filter((o) => o.role === 'director')
                .map((officer) => (
                  <div
                    key={officer.id}
                    onClick={() =>
                      selectedOfficerId === officer.id
                        ? setSelectedOfficerId('')
                        : setSelectedOfficerId(officer.id)
                    }
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedOfficerId === officer.id
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                        : 'hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-gray-900">
                      {officer.name}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {officer.occupation ?? officer.role}
                    </div>
                    {officer.appointedOn && (
                      <div className="text-sm text-gray-500 mt-2">
                        Appointed:{' '}
                        {new Date(officer.appointedOn).toLocaleDateString()}
                      </div>
                    )}
                    {selectedOfficerId === officer.id && (
                      <div className="absolute top-2 right-2 text-blue-500">
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={handleBack}
                className="px-6 py-2 border rounded-md hover:bg-gray-50"
              >
                Back to Search
              </button>
              <button
                onClick={handleClaimBusiness}
                disabled={!selectedOfficerId || isLoading}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-md font-medium transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                Claim Business
              </button>
            </div>

            {!!error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2">Loading business details...</span>
        </div>
      )}
    </SceneContainer>
  )
}

export default RegisterBusinessScreen
